import { AlertTriangle, ArrowRight, Clock, FileText, Route, ShieldCheck, Truck } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { AgentTimeline } from "@/components/agent-timeline/AgentTimeline";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { getEmptyListState } from "@/core/ui/empty-states";
import { CapacityMatchPanel } from "./CapacityMatchPanel";
import { PaymentHoldsPanel } from "./PaymentHoldsPanel";
import { PodUploadPanel } from "./PodUploadPanel";
import type { LoadDetailData } from "./load-detail-types";

export function LoadDetailView({ data }: { data: LoadDetailData }) {
  const { load, caseFile, label } = data;
  const regulatoryTags = load.regulatoryTags ?? [];
  const customerReferences = load.customerReferences ?? [];
  const isDecision = label === "Decision needed";
  const escalated = data.agentState === "escalated";

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {caseFile.agentWord}
          </span>
          <Link className="text-xs text-muted-foreground hover:text-foreground" href="/">
            ← Agenda
          </Link>
        </div>

        <h1 className="mt-3 text-2xl font-semibold tracking-tight">{caseFile.goal}</h1>

        <p
          className={`mt-2 text-sm ${
            isDecision
              ? escalated
                ? "text-red-600"
                : "text-amber-700"
              : "text-muted-foreground"
          }`}
        >
          {caseFile.statusLine}
        </p>

        {isDecision && (
          <section className="mt-6 flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
            <div
              className={`text-xs font-semibold uppercase tracking-wide ${
                escalated ? "text-red-600" : "text-amber-700"
              }`}
            >
              {escalated ? "Escalated — needs you now" : "Decision needed"}
            </div>
            <p className="text-sm leading-relaxed">{caseFile.decisionLine}</p>
            {caseFile.why && (
              <p className="text-sm text-muted-foreground">
                Why: {caseFile.why}
              </p>
            )}
            <div>
              <Link
                href="/inbox"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                Review &amp; decide
                <ArrowRight size={16} />
              </Link>
            </div>
          </section>
        )}

        <section className="mt-6 grid gap-3 md:grid-cols-3">
          <Metric label="Risk" value={`${load.currentRiskScore ?? "-"}%`} />
          <Metric label="Confidence" value={`${load.currentConfidenceScore ?? "-"}%`} />
          <Metric label="Safety" value="read_and_draft" />
        </section>

        <section className="mt-6 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <ShieldCheck size={15} />
                Decision Packet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 p-3 text-sm">
              <div className="rounded-md border border-border p-3">
                <div className="text-xs uppercase text-muted-foreground">Recommended next action</div>
                <p className="mt-1">{data.exceptions[0]?.recommendedAction ?? caseFile.statusLine}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="info">read_and_draft</Badge>
                <Badge variant="warn">approval gated</Badge>
                <Badge variant="warn">no unsafe external action sent</Badge>
              </div>
              {data.approvals.map((approval) => (
                <div key={approval._id} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{approval.title}</span>
                    <Badge variant="warn">{approval.status}</Badge>
                  </div>
                  <p className="mt-1 text-muted-foreground">{approval.riskSummary}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">Evidence</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-3">
              {data.evidence.map((item) => (
                <div key={item._id} className="rounded-md border border-border p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium">{item.source}</div>
                    <Badge variant="info">{item.type}</Badge>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    {typeof item.payload.summary === "string" ? item.payload.summary : "Evidence retained for audit."}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <section className="mt-6">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <ShieldCheck size={15} />
                Agent Work
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 p-3 text-sm md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-xs uppercase text-muted-foreground">Open tasks</div>
                {(data.agentTasks ?? []).filter((task) => task.status !== "completed").slice(0, 4).map((task) => (
                  <div key={task.taskId} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{task.title}</span>
                      <Badge variant={task.status === "needs_approval" ? "warn" : "info"}>{task.status}</Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">{task.recommendedAction}</p>
                  </div>
                ))}
                {(data.agentTasks ?? []).length === 0 ? (
                  <EmptyState className="p-4" {...getEmptyListState("approvals")} />
                ) : null}
              </div>
              <div className="space-y-2">
                <div className="text-xs uppercase text-muted-foreground">Latest decisions</div>
                {(data.agentDecisions ?? []).slice(0, 3).map((decision) => (
                  <div key={decision.decisionId} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{decision.decision}</span>
                      <Badge variant={decision.requiredApproval ? "warn" : "info"}>
                        {decision.requiredApproval ? "approval gated" : "recorded"}
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">{decision.recommendedNextAction}</p>
                    {decision.requiredApproval ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Badge variant="info">read_and_draft</Badge>
                        <Badge variant="warn">no unsafe external action sent</Badge>
                      </div>
                    ) : null}
                  </div>
                ))}
                {(data.toolCallLogs ?? []).slice(0, 3).map((tool) => (
                  <div key={tool.toolCallId} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{tool.toolName}</span>
                      <Badge variant={tool.permissionDecision === "block" ? "bad" : tool.permissionDecision === "approval_required" ? "warn" : "good"}>
                        {tool.status}
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">
                      Policy: {tool.permissionDecision}; no unsafe external action sent.
                    </p>
                  </div>
                ))}
                {(data.loadRiskAssessments ?? []).slice(0, 1).map((risk) => (
                  <div key={risk._id ?? risk.createdAt} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">Carrier risk</span>
                      <Badge variant={risk.recommendation === "block" ? "bad" : risk.recommendation === "approval_required" ? "warn" : "good"}>
                        {risk.riskScore}% risk
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">{risk.recommendedNextAction}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

      </div>

      <details className="group" open>
        <summary className="mx-auto flex max-w-3xl cursor-pointer list-none items-center gap-2 px-6 pb-6 text-sm text-muted-foreground hover:text-foreground">
          <ArrowRight
            size={14}
            className="transition-transform group-open:rotate-90"
          />
          Show the full workflow tree
        </summary>

        <main className="mx-auto grid max-w-7xl grid-cols-[220px_minmax(0,1fr)_340px] gap-4 px-6 py-4">
        <aside className="space-y-2">
          <QuickAction icon={<Truck size={15} />} label="Create dispatch packet" />
          <QuickAction icon={<FileText size={15} />} label="Generate BOL draft" />
          <QuickAction icon={<Clock size={15} />} label="Request ETA" />
          <QuickAction icon={<AlertTriangle size={15} />} label="Open exception" />
          <Link className="block pt-2 text-xs text-muted-foreground hover:text-foreground" href="/inbox">
            Review pending approvals
          </Link>
        </aside>

        <section className="space-y-4">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">Freight</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 p-3 text-sm lg:grid-cols-3">
              <Metric label="Equipment" value={load.equipment?.type ?? load.equipmentType} />
              <Metric label="Commodity" value={load.commodity?.category ?? "general_dry"} />
              <Metric label="Regulatory" value={regulatoryTags.length ? regulatoryTags.join(", ") : "none"} />
              <Metric label="Value" value={formatMoney(load.valueCents)} />
              <Metric label="Risk" value={String(load.currentRiskScore ?? "-")} />
              <Metric label="Confidence" value={String(load.currentConfidenceScore ?? "-")} />
            </CardContent>
          </Card>

          <CapacityMatchPanel load={load} />

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm">Appointments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 p-3 text-sm">
                <StopRow
                  label="Pickup"
                  city={`${load.origin.city}, ${load.origin.state}`}
                  windowStart={load.pickupWindowStart}
                  windowEnd={load.pickupWindowEnd}
                />
                <StopRow
                  label="Delivery"
                  city={`${load.destination.city}, ${load.destination.state}`}
                  windowStart={load.deliveryWindowStart}
                  windowEnd={load.deliveryWindowEnd}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm">Customer References</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-3 text-sm">
                {customerReferences.length === 0 ? (
                  <EmptyState className="p-4" {...getEmptyListState("customerReferences")} />
                ) : (
                  customerReferences.map((reference) => (
                    <div key={`${reference.label}-${reference.value}`} className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{reference.label}</span>
                      <span className="font-medium">{reference.value}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">Charges</CardTitle>
            </CardHeader>
            <CardContent className={data.charges.length === 0 ? "p-3" : "p-0"}>
              {data.charges.length === 0 ? (
                <EmptyState className="p-4" {...getEmptyListState("charges")} />
              ) : (
                <table className="w-full text-left text-sm">
                  <tbody>
                    {data.charges.map((charge) => (
                      <tr key={charge._id} className="border-t border-border">
                        <td className="px-3 py-2">{charge.description}</td>
                        <td className="px-3 py-2 text-muted-foreground">{charge.side}</td>
                        <td className="px-3 py-2 text-muted-foreground">{charge.status}</td>
                        <td className="px-3 py-2 text-right font-medium">{formatMoney(charge.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">Exceptions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-3">
              {data.exceptions.length === 0 ? (
                <EmptyState className="p-4" {...getEmptyListState("exceptions")} />
              ) : (
                data.exceptions.map((exception) => (
                  <div key={exception._id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium">{exception.title}</div>
                      <Badge variant={exception.severity === "critical" ? "bad" : "warn"}>
                        {exception.severity}
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">{exception.recommendedAction}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <PodUploadPanel tenantId={load.tenantId} loadId={load._id} />

          <PaymentHoldsPanel holds={data.paymentHolds ?? []} />
        </section>

        <aside id="timeline">
          <AgentTimeline
            entityType="load"
            entityId={load._id}
            tenantId={load.tenantId}
            fallback={{
              agentActions: data.agentActions,
              auditEvents: data.auditEvents,
              evidence: data.evidence,
              agentDecisions: data.agentDecisions ?? [],
              toolCallLogs: data.toolCallLogs ?? [],
            }}
          />
        </aside>
        </main>
      </details>
    </div>
  );
}

function QuickAction({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <button className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left text-sm hover:bg-muted">
      {icon}
      <span>{label}</span>
    </button>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 truncate font-medium">{value}</div>
    </div>
  );
}

function StopRow({
  label,
  city,
  windowStart,
  windowEnd,
}: {
  label: string;
  city: string;
  windowStart: string;
  windowEnd: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Route className="mt-0.5 text-muted-foreground" size={16} />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-muted-foreground">{city}</div>
        <div className="text-xs text-muted-foreground">
          {formatDate(Date.parse(windowStart))} - {formatDate(Date.parse(windowEnd))}
        </div>
      </div>
    </div>
  );
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatDate(value: number) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(value);
}
